import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";

const QUESTIONS = [
  { key: "purohit", label: "How would you rate your Purohit experience?" },
  { key: "samagri", label: "How satisfied were you with the Puja-Samagri Kit?" },
  { key: "service", label: "How satisfied were you with the Worship / Vedic Ritual experience?" },
];

function StarRow({ value, onChange, testid }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`${testid}-${n}`} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => onChange(n)} className="transition-transform hover:scale-110">
          <Star className={`h-8 w-8 ${(hover || value) >= n ? "fill-accent text-accent" : "text-muted-foreground/40"}`} />
        </button>
      ))}
    </div>
  );
}

export default function RatingDialog({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [scores, setScores] = useState({ purohit: 0, samagri: 0, service: 0 });
  const [review, setReview] = useState("");
  const [saving, setSaving] = useState(false);
  const words = review.trim() ? review.trim().split(/\s+/).length : 0;

  const submit = async () => {
    if (!scores.purohit) { toast.error("Please rate your Purohit experience"); return; }
    if (words > 100) { toast.error("Please keep your review within 100 words"); return; }
    setSaving(true);
    try {
      await api.post("/ratings", {
        booking_id: booking.id, stars_purohit: scores.purohit,
        stars_samagri: scores.samagri, stars_service: scores.service, review,
      });
      toast.success("Thank you for your feedback!");
      setOpen(false); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit feedback");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`rate-${booking.id}`} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"><Star className="h-4 w-4 mr-1" /> Give Feedback</Button>
      </DialogTrigger>
      <DialogContent data-testid="rating-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif">Feedback &amp; Review</DialogTitle>
          <DialogDescription>For {booking.service_name} by {booking.purohit_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {QUESTIONS.map((q, i) => (
            <div key={q.key}>
              <p className="text-sm font-medium mb-2">Q{i + 1}. {q.label}</p>
              <StarRow value={scores[q.key]} onChange={(v) => setScores((s) => ({ ...s, [q.key]: v }))} testid={`fb-${q.key}`} />
            </div>
          ))}
          <div>
            <p className="text-sm font-medium mb-2">Q4. Your experience &amp; review (within 100 words)</p>
            <Textarea data-testid="review-text" value={review} onChange={(e) => setReview(e.target.value)} placeholder="Share a few words about your experience" className="rounded-xl min-h-[100px]" />
            <p className={`text-xs mt-1 text-right ${words > 100 ? "text-destructive" : "text-muted-foreground"}`} data-testid="review-word-count">{words}/100 words</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="rating-submit" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Submitting…</> : "Submit feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
